import type { Ship, ShipModule } from "../types";
import { jitterAngle } from "./config";
import { sphereTrace, type ShipCollider } from "./sdf";
import type { BodyHandle, Contact, SubCollider } from "./types";

/** SDF path reads only `ref`; one shared instance avoids alloc. */
const _sdfSub: SubCollider = {
  hw: 0, hh: 0, cx: 0, cy: 0, rot: 0, alive: true, ref: null,
};

/** No tunneling: CCD walks the swept motion in CCD_STRIDE_M steps. */
const CCD_STRIDE_M = 1;
const CCD_MAX_STEPS = 512;

/** Contact normal points A → B. */
export function narrowPhase(a: BodyHandle, b: BodyHandle): Contact | null {
  const aPoint = a.kind !== "rigid";
  const bPoint = b.kind !== "rigid";
  if (aPoint && !bPoint) {
    const c = (b.ref as Ship | null)?.collider;
    if (c) return pointVsShipSdf(a, b, c, true);
  } else if (bPoint && !aPoint) {
    const c = (a.ref as Ship | null)?.collider;
    if (c) return pointVsShipSdf(b, a, c, false);
  }
  const aHasSubs = a.subs.length > 0;
  const bHasSubs = b.subs.length > 0;
  if (!aHasSubs && !bHasSubs) return circleCircle(a, b);
  if (!aHasSubs) return circleComposite(a, b, true);
  if (!bHasSubs) return circleComposite(b, a, false);
  return compositeComposite(a, b);
}

/** Swept fallback: a 1100 m/s slug would otherwise tunnel a spark. */
function circleCircle(a: BodyHandle, b: BodyHandle): Contact | null {
  const dxE = b.pos.x - a.pos.x;
  const dyE = b.pos.y - a.pos.y;
  const rr = a.pointRadius + b.pointRadius;
  const d2E = dxE * dxE + dyE * dyE;
  if (d2E < rr * rr) {
    const d = Math.sqrt(d2E);
    let nx: number, ny: number;
    if (d > 1e-9) { nx = dxE / d; ny = dyE / d; }
    else { nx = 1; ny = 0; }
    const depth = rr - d;
    const cx = a.pos.x + nx * (a.pointRadius - depth * 0.5);
    const cy = a.pos.y + ny * (a.pointRadius - depth * 0.5);
    return finishContact(a, b, cx, cy, nx, ny, depth, null, null);
  }
  return circleCircleSwept(a, b, rr);
}

function circleCircleSwept(a: BodyHandle, b: BodyHandle, rr: number): Contact | null {
  const px = b.prevPos.x - a.prevPos.x;
  const py = b.prevPos.y - a.prevPos.y;
  const vx = (b.pos.x - b.prevPos.x) - (a.pos.x - a.prevPos.x);
  const vy = (b.pos.y - b.prevPos.y) - (a.pos.y - a.prevPos.y);

  const av = vx * vx + vy * vy;
  if (av < 1e-12) return null;

  const bv = px * vx + py * vy;
  const cv = px * px + py * py - rr * rr;
  const disc = bv * bv - av * cv;
  if (disc < 0) return null;
  const sd = Math.sqrt(disc);
  const tEnter = (-bv - sd) / av;
  const tExit = (-bv + sd) / av;
  if (tEnter > 1 || tExit < 0) return null;
  const t = tEnter < 0 ? 0 : tEnter;
  if (t <= 0 || t >= 1) return null;

  const aTx = a.prevPos.x + t * (a.pos.x - a.prevPos.x);
  const aTy = a.prevPos.y + t * (a.pos.y - a.prevPos.y);
  const bTx = b.prevPos.x + t * (b.pos.x - b.prevPos.x);
  const bTy = b.prevPos.y + t * (b.pos.y - b.prevPos.y);
  const ddx = bTx - aTx;
  const ddy = bTy - aTy;
  const d2 = ddx * ddx + ddy * ddy;
  let nx: number, ny: number;
  if (d2 > 1e-18) {
    const d = Math.sqrt(d2);
    nx = ddx / d;
    ny = ddy / d;
  } else {
    nx = 1; ny = 0;
  }
  const cx = aTx + nx * a.pointRadius;
  const cy = aTy + ny * a.pointRadius;

  a.pos.x = aTx; a.pos.y = aTy;
  b.pos.x = bTx; b.pos.y = bTy;
  a.toi *= t;
  b.toi *= t;
  return finishContact(a, b, cx, cy, nx, ny, 0, null, null);
}

/** aIsCircle flips final normal to keep A→B. */
function circleComposite(
  small: BodyHandle,
  big: BodyHandle,
  aIsCircle: boolean,
): Contact | null {
  const dxSm = small.pos.x - small.prevPos.x;
  const dySm = small.pos.y - small.prevPos.y;
  const dxBg = big.pos.x - big.prevPos.x;
  const dyBg = big.pos.y - big.prevPos.y;
  const dRotBg = big.rot - big.prevRot;
  const relLen = Math.hypot(dxSm - dxBg, dySm - dyBg);
  const steps = relLen > CCD_STRIDE_M
    ? Math.min(CCD_MAX_STEPS, Math.ceil(relLen / CCD_STRIDE_M))
    : 1;
  const rr = small.radius + big.radius;

  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const smX = small.prevPos.x + t * dxSm;
    const smY = small.prevPos.y + t * dySm;
    const bgX = big.prevPos.x + t * dxBg;
    const bgY = big.prevPos.y + t * dyBg;
    // Proximity gate: far substeps skip OBB scan.
    const dx = bgX - smX, dy = bgY - smY;
    if (dx * dx + dy * dy > rr * rr) continue;

    const bgRot = big.prevRot + t * dRotBg;
    const hit = circleCompositeAt(small, big, smX, smY, bgX, bgY, bgRot, aIsCircle);
    if (!hit) continue;

    // Rewind both to TOI for a consistent downstream snapshot.
    if (t < 1) {
      small.pos.x = smX; small.pos.y = smY;
      big.pos.x = bgX; big.pos.y = bgY;
      big.rot = bgRot;
      small.toi *= t;
      big.toi *= t;
    }
    return hit;
  }
  return null;
}

/** Positions passed explicitly so substep calls don't mutate shared Vec2. */
function circleCompositeAt(
  small: BodyHandle, big: BodyHandle,
  smX: number, smY: number,
  bgX: number, bgY: number, bgRot: number,
  aIsCircle: boolean,
): Contact | null {
  bgRot = jitterAngle(bgRot);
  const dx = smX - bgX;
  const dy = smY - bgY;
  const cR = Math.cos(-bgRot);
  const sR = Math.sin(-bgRot);
  const lpx = dx * cR - dy * sR;
  const lpy = dx * sR + dy * cR;
  const r = small.pointRadius;

  let bestPen = -Infinity;
  let bestNxLocal = 0;
  let bestNyLocal = 0;
  let bestCxLocal = 0;
  let bestCyLocal = 0;
  let bestSub: SubCollider | null = null;

  for (const sub of big.subs) {
    if (!sub.alive) continue;
    const sdx = lpx - sub.cx;
    const sdy = lpy - sub.cy;
    const sc = Math.cos(-sub.rot);
    const ss = Math.sin(-sub.rot);
    const relX = sdx * sc - sdy * ss;
    const relY = sdx * ss + sdy * sc;
    const cxBox = clamp(relX, -sub.hw, sub.hw);
    const cyBox = clamp(relY, -sub.hh, sub.hh);
    const dxBox = relX - cxBox;
    const dyBox = relY - cyBox;
    const d2 = dxBox * dxBox + dyBox * dyBox;

    if (d2 < r * r) {
      const inside = d2 < 1e-12;
      let nxBox: number, nyBox: number, depth: number;
      if (inside) {
        const dL = sub.hw + relX;
        const dR = sub.hw - relX;
        const dT = sub.hh + relY;
        const dB = sub.hh - relY;
        const m = Math.min(dL, dR, dT, dB);
        if (m === dL) { nxBox = -1; nyBox = 0; }
        else if (m === dR) { nxBox = 1; nyBox = 0; }
        else if (m === dT) { nxBox = 0; nyBox = -1; }
        else { nxBox = 0; nyBox = 1; }
        depth = m + r;
      } else {
        const d = Math.sqrt(d2);
        nxBox = dxBox / d;
        nyBox = dyBox / d;
        depth = r - d;
      }
      if (depth > bestPen) {
        bestPen = depth;
        const cS = Math.cos(sub.rot);
        const sS = Math.sin(sub.rot);
        bestNxLocal = nxBox * cS - nyBox * sS;
        bestNyLocal = nxBox * sS + nyBox * cS;
        bestCxLocal = sub.cx + cxBox * cS - cyBox * sS;
        bestCyLocal = sub.cy + cxBox * sS + cyBox * cS;
        bestSub = sub;
      }
    }
  }

  if (bestPen <= 0 || !bestSub) return null;

  const cR2 = Math.cos(bgRot);
  const sR2 = Math.sin(bgRot);
  const cxWorld = bgX + bestCxLocal * cR2 - bestCyLocal * sR2;
  const cyWorld = bgY + bestCxLocal * sR2 + bestCyLocal * cR2;
  const nxWorld = bestNxLocal * cR2 - bestNyLocal * sR2;
  const nyWorld = bestNxLocal * sR2 + bestNyLocal * cR2;

  if (aIsCircle) {
    return finishContactAt(small, big, cxWorld, cyWorld, -nxWorld, -nyWorld,
                           bestPen, null, bestSub,
                           smX, smY, bgX, bgY);
  }
  return finishContactAt(big, small, cxWorld, cyWorld, nxWorld, nyWorld,
                         bestPen, bestSub, null,
                         bgX, bgY, smX, smY);
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

const _axes = new Float64Array(8);

function compositeComposite(a: BodyHandle, b: BodyHandle): Contact | null {
  const dxA = a.pos.x - a.prevPos.x;
  const dyA = a.pos.y - a.prevPos.y;
  const dxB = b.pos.x - b.prevPos.x;
  const dyB = b.pos.y - b.prevPos.y;
  const dRotA = a.rot - a.prevRot;
  const dRotB = b.rot - b.prevRot;
  const relLen = Math.hypot(dxA - dxB, dyA - dyB);
  const steps = relLen > CCD_STRIDE_M
    ? Math.min(CCD_MAX_STEPS, Math.ceil(relLen / CCD_STRIDE_M))
    : 1;
  const rr = a.radius + b.radius;

  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const aX = a.prevPos.x + t * dxA;
    const aY = a.prevPos.y + t * dyA;
    const bX = b.prevPos.x + t * dxB;
    const bY = b.prevPos.y + t * dyB;
    const ddx = bX - aX, ddy = bY - aY;
    if (ddx * ddx + ddy * ddy > rr * rr) continue;

    const aRot = a.prevRot + t * dRotA;
    const bRot = b.prevRot + t * dRotB;
    const hit = compositeCompositeAt(a, b, aX, aY, aRot, bX, bY, bRot);
    if (!hit) continue;

    if (t < 1) {
      a.pos.x = aX; a.pos.y = aY; a.rot = aRot;
      b.pos.x = bX; b.pos.y = bY; b.rot = bRot;
      a.toi *= t;
      b.toi *= t;
    }
    return hit;
  }
  return null;
}

/** Single deepest OBB pair (per-pair impulses would over-correct clusters). */
function compositeCompositeAt(
  a: BodyHandle, b: BodyHandle,
  aX: number, aY: number, aRot: number,
  bX: number, bY: number, bRot: number,
): Contact | null {
  aRot = jitterAngle(aRot);
  bRot = jitterAngle(bRot);
  const cA = Math.cos(aRot);
  const sA = Math.sin(aRot);
  const cB = Math.cos(bRot);
  const sB = Math.sin(bRot);

  let bestDepth = 0;
  let bestNx = 0;
  let bestNy = 0;
  let bestCx = 0;
  let bestCy = 0;
  let bestSubA: SubCollider | null = null;
  let bestSubB: SubCollider | null = null;

  for (const sA1 of a.subs) {
    if (!sA1.alive) continue;
    const rotA = aRot + sA1.rot;
    const uAx = Math.cos(rotA), uAy = Math.sin(rotA);
    const vAx = -uAy, vAy = uAx;
    const aCx = aX + sA1.cx * cA - sA1.cy * sA;
    const aCy = aY + sA1.cx * sA + sA1.cy * cA;
    const aCirc = Math.hypot(sA1.hw, sA1.hh);

    for (const sB1 of b.subs) {
      if (!sB1.alive) continue;
      const bCx = bX + sB1.cx * cB - sB1.cy * sB;
      const bCy = bY + sB1.cx * sB + sB1.cy * cB;
      const ddx = bCx - aCx;
      const ddy = bCy - aCy;
      const bCirc = Math.hypot(sB1.hw, sB1.hh);
      const rr = aCirc + bCirc;
      if (ddx * ddx + ddy * ddy > rr * rr) continue;

      const rotB = bRot + sB1.rot;
      const uBx = Math.cos(rotB), uBy = Math.sin(rotB);
      const vBx = -uBy, vBy = uBx;

      let minPen = Infinity;
      let nx = 0, ny = 0;
      let ok = true;
      _axes[0] = uAx; _axes[1] = uAy;
      _axes[2] = vAx; _axes[3] = vAy;
      _axes[4] = uBx; _axes[5] = uBy;
      _axes[6] = vBx; _axes[7] = vBy;
      for (let k = 0; k < 4; k++) {
        const ax = _axes[k * 2];
        const ay = _axes[k * 2 + 1];
        const projA = sA1.hw * Math.abs(uAx * ax + uAy * ay) + sA1.hh * Math.abs(vAx * ax + vAy * ay);
        const projB = sB1.hw * Math.abs(uBx * ax + uBy * ay) + sB1.hh * Math.abs(vBx * ax + vBy * ay);
        const dot = ddx * ax + ddy * ay;
        const sep = Math.abs(dot) - (projA + projB);
        if (sep >= 0) { ok = false; break; }
        const pen = -sep;
        if (pen < minPen) {
          minPen = pen;
          const sign = dot >= 0 ? 1 : -1;
          nx = ax * sign;
          ny = ay * sign;
        }
      }
      if (!ok) continue;

      if (minPen > bestDepth) {
        bestDepth = minPen;
        bestNx = nx;
        bestNy = ny;
        bestCx = (aCx + bCx) * 0.5;
        bestCy = (aCy + bCy) * 0.5;
        bestSubA = sA1;
        bestSubB = sB1;
      }
    }
  }

  if (!bestSubA || !bestSubB) return null;
  return finishContactAt(a, b, bestCx, bestCy, bestNx, bestNy, bestDepth,
                         bestSubA, bestSubB, aX, aY, bX, bY);
}

function finishContact(
  a: BodyHandle, b: BodyHandle,
  cx: number, cy: number,
  nx: number, ny: number,
  depth: number,
  subA: SubCollider | null, subB: SubCollider | null,
): Contact {
  return finishContactAt(a, b, cx, cy, nx, ny, depth, subA, subB,
                         a.pos.x, a.pos.y, b.pos.x, b.pos.y);
}

function pointVsShipSdf(
  point: BodyHandle, ship: BodyHandle,
  collider: ShipCollider,
  aIsPoint: boolean,
): Contact | null {
  // Swept segment in ship-local meters (slow-rotation regime).
  const dpx0 = point.prevPos.x - ship.prevPos.x;
  const dpy0 = point.prevPos.y - ship.prevPos.y;
  const cp = Math.cos(-ship.prevRot);
  const sp = Math.sin(-ship.prevRot);
  const lx0 = dpx0 * cp - dpy0 * sp;
  const ly0 = dpx0 * sp + dpy0 * cp;

  const dpx1 = point.pos.x - ship.pos.x;
  const dpy1 = point.pos.y - ship.pos.y;
  const cr = Math.cos(-ship.rot);
  const sr = Math.sin(-ship.rot);
  const lx1 = dpx1 * cr - dpy1 * sr;
  const ly1 = dpx1 * sr + dpy1 * cr;

  const hit = sphereTrace(collider, lx0, ly0, lx1, ly1);
  if (!hit || !hit.owner) return null;

  const t = hit.t;
  if (t < 1) {
    point.pos.x = point.prevPos.x + t * (point.pos.x - point.prevPos.x);
    point.pos.y = point.prevPos.y + t * (point.pos.y - point.prevPos.y);
    point.toi *= t;
    ship.pos.x = ship.prevPos.x + t * (ship.pos.x - ship.prevPos.x);
    ship.pos.y = ship.prevPos.y + t * (ship.pos.y - ship.prevPos.y);
    ship.rot = ship.prevRot + t * (ship.rot - ship.prevRot);
    ship.toi *= t;
  }

  const cR = Math.cos(ship.rot);
  const sR = Math.sin(ship.rot);
  const cxWorld = ship.pos.x + hit.lx * cR - hit.ly * sR;
  const cyWorld = ship.pos.y + hit.lx * sR + hit.ly * cR;
  const nxWorld = hit.nx * cR - hit.ny * sR;
  const nyWorld = hit.nx * sR + hit.ny * cR;

  _sdfSub.ref = hit.owner as ShipModule;

  // Gradient points ship→point; flip when point is A to keep A→B.
  if (aIsPoint) {
    return finishContactAt(point, ship, cxWorld, cyWorld,
                           -nxWorld, -nyWorld, 0,
                           null, _sdfSub,
                           point.pos.x, point.pos.y, ship.pos.x, ship.pos.y);
  }
  return finishContactAt(ship, point, cxWorld, cyWorld,
                         nxWorld, nyWorld, 0,
                         _sdfSub, null,
                         ship.pos.x, ship.pos.y, point.pos.x, point.pos.y);
}

/** Explicit centers so CCD contacts use TOI pose for moment arms. */
function finishContactAt(
  a: BodyHandle, b: BodyHandle,
  cx: number, cy: number,
  nx: number, ny: number,
  depth: number,
  subA: SubCollider | null, subB: SubCollider | null,
  aX: number, aY: number, bX: number, bY: number,
): Contact {
  const rAx = cx - aX;
  const rAy = cy - aY;
  const rBx = cx - bX;
  const rBy = cy - bY;
  const vAx = a.vel.x - a.angVel * rAy;
  const vAy = a.vel.y + a.angVel * rAx;
  const vBx = b.vel.x - b.angVel * rBy;
  const vBy = b.vel.y + b.angVel * rBx;
  const dvx = vBx - vAx;
  const dvy = vBy - vAy;
  const vClosing = -(dvx * nx + dvy * ny);
  return { a, b, cx, cy, nx, ny, depth, vClosing, subA, subB };
}
